import { getApiInstance } from '../axiosInterceptor'
import axios from 'axios'

/**
 * Get API endpoint URL
 * @returns {string} The base API URL
 */
const getApiEndpoint = () => {
    return process.env.REACT_APP_API_ENDPOINT || 'https://gw.dev.apim.kmart.com.au/kolumbus/api/v2'
}

/**
 * Create a new vendor (POST /rfp/createVendor)
 * @param {Object} vendorData - Vendor form data
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with vendor creation result
 */
export const createVendor = async (vendorData, msalInstance) => {
  try {
    const formData = new FormData()

    // Add vendor data to form data - matching the API screenshot field names
    formData.append('vendorName', vendorData.vendorName || '')
    formData.append('vendorAddress', vendorData.vendorAddress || '')
    formData.append('tinNumber', vendorData.tinNo || '')
    formData.append('sapReferenceId', vendorData.sapReferenceId || 'NA')

    // Add BIR attachment if provided
    if (vendorData.birAttachment && vendorData.birAttachment.length > 0) {
      const birFile = vendorData.birAttachment[0]
      const fileObj = birFile.originFileObj || birFile
      formData.append('birAttachment', fileObj, fileObj.name)
    }

    // Add additional attachments if provided
    if (vendorData.generalAttachments && vendorData.generalAttachments.length > 0) {
      vendorData.generalAttachments.forEach((file) => {
        const fileObj = file.originFileObj || file
        formData.append('additionalAttachments', fileObj, fileObj.name)
      })
    }

    const apiUrl = `${getApiEndpoint()}/rfp/createVendor`

    // Explicitly set headers to ensure proper formatting
    const headers = {
      'Accept': 'application/json'
    }

    // If MSAL instance is provided, use it to get the API instance
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)

      const response = await axiosInstance.post(apiUrl, formData, {
        headers: headers
      })
      return response.data
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')

      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.post(apiUrl, formData, {
        headers: headers
      })

      return response.data
    }
  } catch (error) {
    console.error('Error creating vendor:', error)
    throw error
  }
}

/**
 * Update an existing vendor (POST /rfp/updateVendorDetails)
 * @param {string} vendorId - ID of the vendor to update
 * @param {Object} vendorData - Updated vendor form data
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {boolean} statusOnly - Whether this is only a status update
 * @returns {Promise} - Promise that resolves with vendor update result
 */
export const updateVendor = async (vendorId, vendorData, msalInstance, statusOnly = false) => {
  try {
    if (!vendorId) {
      throw new Error('Vendor ID is required for update')
    }

    console.log('Update vendor called with:', { vendorId, vendorData, statusOnly })

    const apiUrl = `${getApiEndpoint()}/rfp/updateVendorDetails`
    console.log('API URL:', apiUrl)

    let payload

    if (statusOnly) {
      // For status-only updates: updateStatus = "true", only vendorID and status required
      payload = {
        vendorID: vendorId,
        updateStatus: 'true',
        status: vendorData.status ? 'true' : 'false'
      }
      console.log('Status-only update payload:', payload)
    } else {
      // For vendor data updates: updateStatus = "false"
      // Only include fields that are actually being updated
      payload = {
        vendorID: vendorId,
        updateStatus: 'false'
      }

      // Track if any fields are being updated
      let hasUpdates = false

      // Add vendor details - only include fields that are provided and not empty
      if (vendorData.vendorName !== undefined && vendorData.vendorName !== null && vendorData.vendorName !== '') {
        payload.vendorName = vendorData.vendorName
        hasUpdates = true
      }

      if (vendorData.vendorAddress !== undefined && vendorData.vendorAddress !== null && vendorData.vendorAddress !== '') {
        payload.vendorAddress = vendorData.vendorAddress
        hasUpdates = true
      }

      // Handle both tinNumber and tinNo field names - TIN is completely optional
      const tinValue = vendorData.tinNumber || vendorData.tinNo
      if (tinValue !== undefined && tinValue !== null && tinValue !== '') {
        payload.tinNumber = tinValue
        hasUpdates = true
      }

      // Add SAP Reference ID if provided - completely optional
      if (vendorData.sapReferenceId !== undefined && vendorData.sapReferenceId !== null) {
        payload.sapReferenceId = vendorData.sapReferenceId || 'NA'
        hasUpdates = true
      }

      // Validate that at least one field is being updated (TIN and SAP Reference ID are not mandatory)
      if (!hasUpdates) {
        throw new Error('At least one field (vendorName or vendorAddress) must be provided for update')
      }

      console.log('Vendor data update payload:', payload)
      console.log('Fields being updated:', Object.keys(payload).filter(key => key !== 'vendorID' && key !== 'updateStatus'))
    }

    // Set headers for JSON content
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // If MSAL instance is provided, use it to get the API instance
    if (msalInstance) {
      console.log('Using MSAL instance for authentication')
      const axiosInstance = getApiInstance(msalInstance)

      const response = await axiosInstance.post(apiUrl, payload, {
        headers: headers
      })
      console.log('Update response:', response.data)
      return response.data
    } else {
      console.log('Using localStorage token for authentication')
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')

      if (!token) {
        throw new Error('No authentication token available')
      }

      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.post(apiUrl, payload, {
        headers: headers
      })
      console.log('Update response:', response.data)
      return response.data
    }
  } catch (error) {
    console.error('Error updating vendor:', error)
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      statusText: error.response?.statusText,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        data: error.config?.data,
        headers: error.config?.headers
      }
    })

    // If it's a network error, provide more helpful error message
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Network connection failed. Please check your internet connection and try again.')
    }

    // If it's a 400 error, provide more specific feedback
    if (error.response?.status === 400) {
      const errorMsg = error.response?.data?.message || 'Invalid request data. Please check the form fields and try again.'
      throw new Error(errorMsg)
    }

    // If it's a 401 error, authentication issue
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.')
    }

    // If it's a 403 error, permission issue
    if (error.response?.status === 403) {
      throw new Error('You do not have permission to perform this action.')
    }

    // If it's a 404 error, vendor not found
    if (error.response?.status === 404) {
      throw new Error('Vendor not found. Please refresh the page and try again.')
    }

    // If it's a 500 error, server issue
    if (error.response?.status >= 500) {
      throw new Error('Server error occurred. Please try again later.')
    }

    throw error
  }
}

/**
 * Fetch vendor list with server-side pagination (GET /rfp/fetchVendorList)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {Object} params - Query parameters object
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Number of items per page (default: 10)
 * @returns {Promise} - Promise that resolves with vendor list data
 */

export const fetchVendorList = async (msalInstance) => {
  try {
    const axiosInstance = getApiInstance(msalInstance)
    const baseUrl = `${getApiEndpoint()}/rfp/fetchVendorList`

    // Fetch page 1 first to get total pages
    const page1Response = await axiosInstance.get(`${baseUrl}?page=1&limit=100`)
    const { data: page1Data, pagination } = page1Response.data

    const totalPages = pagination?.totalPages || 1
    if (totalPages <= 1) {
      return page1Response.data
    }

    // Fetch remaining pages in parallel
    const remainingPages = []
    for (let page = 2; page <= totalPages; page++) {
      remainingPages.push(axiosInstance.get(`${baseUrl}?page=${page}&limit=100`))
    }
    const responses = await Promise.all(remainingPages)
    const allData = [...(page1Data || [])]
    responses.forEach((res) => {
      const pageData = res.data?.data || []
      allData.push(...pageData)
    })

    return { data: allData }
  } catch (error) {
    console.error('Error fetching vendor list:', error)
    throw error
  }
}

/**
 * Update specific vendor fields (POST /rfp/updateVendorDetails)
 * @param {string} vendorId - ID of the vendor to update
 * @param {Object} fieldsToUpdate - Object containing only the fields to update
 * @param {string} fieldsToUpdate.vendorName - Optional: New vendor name
 * @param {string} fieldsToUpdate.vendorAddress - Optional: New vendor address  
 * @param {string} fieldsToUpdate.tinNumber - Optional: New TIN number (not mandatory)
 * @param {string} fieldsToUpdate.sapReferenceId - Optional: New SAP Reference ID (not mandatory)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with vendor update result
 */
export const updateVendorFields = async (vendorId, fieldsToUpdate, msalInstance) => {
  try {
    if (!vendorId) {
      throw new Error('Vendor ID is required for update')
    }

    // Validate that at least one field is provided (TIN and SAP Reference ID are optional, not mandatory)
    const mandatoryFields = ['vendorName', 'vendorAddress'] // TIN and SAP Reference ID removed from mandatory list
    const optionalFields = ['tinNumber', 'sapReferenceId'] // TIN and SAP Reference ID are now optional
    const allAllowedFields = [...mandatoryFields, ...optionalFields]

    const providedFields = Object.keys(fieldsToUpdate).filter(key =>
      allAllowedFields.includes(key) &&
      fieldsToUpdate[key] !== undefined &&
      fieldsToUpdate[key] !== null &&
      fieldsToUpdate[key] !== ''
    )

    // Check if at least one mandatory field is provided (vendorName OR vendorAddress)
    const providedMandatoryFields = providedFields.filter(field => mandatoryFields.includes(field))

    if (providedMandatoryFields.length === 0) {
      throw new Error('At least one field (vendorName or vendorAddress) must be provided for update. TIN number and SAP Reference ID are optional.')
    }

    console.log('Updating vendor fields:', { vendorId, fieldsToUpdate: providedFields })

    // Use the existing updateVendor function with statusOnly = false
    return await updateVendor(vendorId, fieldsToUpdate, msalInstance, false)
  } catch (error) {
    console.error('Error updating vendor fields:', error)
    throw error
  }
}

/**
 * Toggle vendor status (POST /rfp/updateVendorDetails)
 * @param {string} vendorID - ID of the vendor
 * @param {boolean} status - New status (true for active, false for inactive)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} - Promise that resolves with update result
 */
export const toggleVendorStatus = async (vendorID, status, msalInstance) => {
  try {
    if (!vendorID) {
      throw new Error('Vendor ID is required')
    }

    console.log('Toggling vendor status:', { vendorID, status })

    // Use the updateVendor function with statusOnly flag set to true
    // This will create payload with updateStatus: "true" and only status field
    const result = await updateVendor(vendorID, { status }, msalInstance, true)

    console.log('Vendor status toggle successful:', result)
    return result
  } catch (error) {
    console.error('Error toggling vendor status:', error)
    throw error
  }
}

/**
 * Handle vendor attachments (upload/delete)
 * @param {string} _id - ID of the vendor
 * @param {string} actionType - Action type ('update' or 'delete')
 * @param {string} attachmentId - Attachment ID (required for delete)
 * @param {File} newFile - New file to upload (required for update)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} Promise that resolves with the operation result
 */
export const handleAttachments = async (_id, actionType, attachmentId = null, newFile = null, msalInstance, attachmentType = null) => {
  try {
    if (!_id) {
      throw new Error('Vendor ID is required')
    }

    if (!actionType || (actionType !== 'update' && actionType !== 'delete')) {
      throw new Error('Valid action type (update or delete) is required')
    }

    if (actionType === 'delete' && !attachmentId) {
      throw new Error('Attachment ID is required for delete operations')
    }

    if (actionType === 'update' && !newFile) {
      throw new Error('File is required for update operations')
    }

    const apiUrl = `${getApiEndpoint()}/generic/handleAttachments`

    // Create form data for the request
    const formData = new FormData()

    // Add required fields - use _id as txnId and set type to 'vendor'
    formData.append('txnId', _id)
    formData.append('type', 'vendor')

    // Add attachmentType if provided (for BIR attachments)
    if (attachmentType) {
      formData.append('attachmentType', attachmentType)
    }

    if (actionType === 'delete') {
      formData.append('actionType', 'delete')
      formData.append('attachmentId', attachmentId)
    } else if (actionType === 'update') {
      formData.append('actionType', 'update')
      formData.append('newFile', newFile, newFile.name) // Include original filename
    }

    // Make the API request with appropriate headers
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, formData, {
        headers: {
          'Accept': 'application/json'
          // Don't set Content-Type here, it will be set automatically for FormData
        }
      })
      return response.data
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
          // Don't set Content-Type here, it will be set automatically for FormData
        }
      })
      return response.data
    }
  } catch (error) {
    console.error('Error handling attachments:', error)
    throw error
  }
}

/**
 * Generate a pre-signed URL for an attachment
 * @param {string} attachmentS3Path - The S3 path of the attachment
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise<string>} - Promise that resolves with the pre-signed URL
 */
export const generatePresignedUrl = async (attachmentS3Path, msalInstance) => {
  try {
    if (!attachmentS3Path) {
      throw new Error('Attachment S3 path is required')
    }

    const apiUrl = `${getApiEndpoint()}/generic/generatePreSignedUrl`

    const payload = {
      attachmentS3Path: attachmentS3Path
    }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, payload)
      return response.data.preSignedUrl
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data.preSignedUrl
    }
  } catch (error) {
    console.error('Error generating pre-signed URL:', error)
    throw error
  }
}
/**
 * Search vendor by search term (GET /rfp/searchVendor)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {string} searchTerm - Search term (e.g. Vendor ID, vendor name)
 * @returns {Promise} - Promise that resolves with matching vendor data
 */
export const searchVendor = async (msalInstance, searchTerm) => {
  try {
    if (!searchTerm?.trim()) {
      throw new Error('Search term is required')
    }

    const apiUrl = `${getApiEndpoint()}/rfp/searchVendor?searchTerm=${encodeURIComponent(searchTerm.trim())}`
    const axiosInstance = getApiInstance(msalInstance)
    const response = await axiosInstance.get(apiUrl)
    return response.data
  } catch (error) {
    if (error.response) {
      console.error('Error searching vendor:', error.response.data)
    }
    throw error
  }
}
/**
 * Export vendor list as Excel file (GET /rfp/exportExcel?type=vendor)
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise<Blob>} - Promise that resolves with the Excel file blob
 */
export const exportVendorExcel = async (msalInstance) => {
  try {
    const apiUrl = `${getApiEndpoint()}/rfp/exportExcel?type=vendor`

    const axiosInstance = getApiInstance(msalInstance)
    const response = await axiosInstance.get(apiUrl, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream'
      }
    })

    return response.data
  } catch (error) {
    if (error.response) {
      console.error('Error exporting Vendor Excel:', error.response.data)
    }
    throw error
  }
}